/**
 * Synthesize a dataSource's default renderer child.
 *
 * A `dataSource` is alias-like: it owns cells transparently and
 * auto-hydrates a child renderer that paints its slot and edits its
 * cells (renderer lookup: `renderer-lookup.ts`; alias hydration:
 * `sheets/.docs/content/rows-aliases.md`). This module mints the child
 * row — its `RowLocalId`, its `RendererDescriptor`, and its concrete
 * Column* instance — so the hydrator can insert it under its source
 * in one pass.
 *
 * Scope: only sources in the `column` slot with a lookup-table entry
 * get a synthesized child. Obj dtypes, unsupported slots, and
 * non-user parent id variants (lookup/reserved/system) return `null`.
 */

import type { Option } from "../../core/option.ts";
import type { RendererDescriptor } from "../descriptor.ts";
import type { JsonObject } from "../../core/json.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import { SYNTHETIC_PRESET } from "../../ids/row-local-id.ts";
import type { LayoutKind, RendererId } from "../slot.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import type { ModelRow } from "../model-row.ts";

// `isSyntheticDefaultRendererId` lives in the leaf `row-local-id` module
// (so `RendererRow` can import it without a cycle through this
// variant-heavy module). Re-exported here for the original import path.
export { isSyntheticDefaultRendererId } from "../../ids/row-local-id.ts";

import { ColumnLogsRow } from "./column-logs.ts";
import { ColumnTimelineRow } from "./column-timeline.ts";
import { ColumnTimezoneRow } from "./column-timezone.ts";
import { ColumnWeatherRow } from "./column-weather.ts";
import { CanvasBarRow } from "./canvas-bar-row.ts";
import { CanvasHRuleRow } from "./canvas-hrule-row.ts";
import { CanvasLineRow } from "./canvas-line-row.ts";
import { CanvasPointsRow } from "./canvas-points-row.ts";
import { CanvasRegressionBandRow } from "./canvas-regression-band-row.ts";
import { CanvasRegressionCurveRow } from "./canvas-regression-curve-row.ts";
import { CanvasRegressionFillRow } from "./canvas-regression-fill-row.ts";
import { ColumnCheckmarkRow } from "./column-checkmark.ts";
import { ColumnEventListRow } from "./column-event-list.ts";
import { ColumnGraphRow } from "./column-graph.ts";
import { ColumnHeatmapRow } from "./column-heatmap.ts";
import { ColumnNumberRow } from "./column-number.ts";
import { ColumnSeriesSummaryRow } from "./column-series-summary.ts";
import { ColumnStickerRow } from "./column-sticker.ts";
import { ColumnTaskRow } from "./column-task.ts";
import { ColumnTextRow } from "./column-text.ts";
import { GridEventRow } from "./grid-event-row.ts";
import { GridTextRow } from "./grid-text-row.ts";
import { ListDataSource } from "./list-data-source.ts";
import { resolveDefaultRendererId } from "./renderer-lookup.ts";
import type { SourceRow } from "./source-row.ts";
import { TimelineEventRow } from "./timeline-event-row.ts";
import { TimelineNumberRow } from "./timeline-number-row.ts";
import { TimelineSpanRow } from "./timeline-span-row.ts";
import { TimelineTextRow } from "./timeline-text-row.ts";

/**
 * Slot a source resolves to when it has no ancestor layout — the
 * sheet-root convention (`column`), per the consolidated rowkey README.
 */
const DEFAULT_SLOT: LayoutKind = "column";

/**
 * Mint the default renderer child for `source`, or return `null`
 * when the lookup defines none.
 *
 * The child's `RowLocalId` is `virtual(r_<parentHex>|renderer)` —
 * reusing the existing virtual-row id convention so the child is
 * deterministic and round-trips through serialize → hydrate without
 * a new id scheme. Parents whose key has no hex anchor (lookup,
 * reserved, system) skip synthesis until a wider id scheme lands;
 * in practice those are obj-dtype rows today, which also skip via
 * the lookup returning `null`.
 */
/**
 * Narrow a row's server-owned metadata bag to its `shape` string
 * (`meta.shape`, e.g. `task/v1`) — the lookup's key for obj sources.
 * Callers pass the shape explicitly because at initial `RawSheet.hydrate`
 * time `rowMetadata` isn't seeded yet (synthesis runs before the metadata
 * pass), so the shape must come straight from the raw row's `meta`.
 */
export function shapeFromMeta(
  meta: Option<JsonObject>,
): Option<string> {
  const shape = meta?.shape;
  return typeof shape === "string" ? shape : null;
}

/**
 * Creates a source row's default renderer child (its id, descriptor and concrete row), which
 * hydration calls for each `dataSource`.
 */
export function hydrateDefaultRenderer(
  source: SourceRow,
  sheet: ModelSheet,
  shape: Option<string>,
  slot: LayoutKind = DEFAULT_SLOT,
): Option<ModelRow> {
  // Aggregate constituents don't render independently — the aggregate's
  // renderers hang off the `list` parent (see `ListDataSource`), reading one
  // merged stream. Skip auto-hydration so e.g. the `tasks` source inside
  // `alias.task-list` doesn't synthesize a renderer alongside the list's own.
  if (source.parent instanceof ListDataSource) return null;

  const rendererId = resolveDefaultRendererId(source.descriptor, slot, shape);
  if (rendererId == null) return null;

  const parentHex = source.id.key.toHex();
  if (parentHex === null) return null;

  const childRaw = `virtual(r_${parentHex}|${SYNTHETIC_PRESET})`;
  const childId = sheet.registry.resolveFromRawId(childRaw);

  const descriptor: RendererDescriptor = {
    role: "renderer",
    rendererId,
  };

  return instantiateRenderer(rendererId, childId, descriptor, sheet);
}

/**
 * Concrete-class dispatch for `column.*` renderer ids. Extracted so
 * `parseRenderer` (registry.ts) can reuse it when hydrating
 * serialized renderer rows — a renderer row that was persisted into
 * `RawSheet` (from a previous `serialize`) needs the same Column*
 * class back on re-hydrate.
 *
 * Returns `null` for ids we don't have a concrete class for yet
 * (the generic `RendererRow` fallback covers them in `parseRenderer`).
 */
/**
 * Dispatch a `rendererId` to its concrete variant class. Returns
 * `null` for unknown ids — the caller in `registry.ts:parseRenderer`
 * falls back to the generic `RendererRow` so unmapped ids round-
 * trip without data loss.
 *
 * Both `timeline[row].*` and `timeline[column].*` resolve through
 * the same class per-renderer-type (e.g. both `timeline[row].event`
 * and `timeline[column].event` → `TimelineEventRow`). The orientation
 * lives on the parent layout's descriptor; the renderer class
 * doesn't need to specialize.
 */
export function instantiateRenderer(
  rendererId: RendererId,
  id: RowLocalId,
  descriptor: RendererDescriptor,
  sheet: ModelSheet,
): Option<ModelRow> {
  switch (rendererId) {
    // column slot
    case "column.text":
      return new ColumnTextRow(id, descriptor, sheet);
    case "column.number":
      return new ColumnNumberRow(id, descriptor, sheet);
    case "column.checkmark":
      return new ColumnCheckmarkRow(id, descriptor, sheet);
    case "column.graph":
      return new ColumnGraphRow(id, descriptor, sheet);
    case "column.series-summary":
      return new ColumnSeriesSummaryRow(id, descriptor, sheet);
    case "column.heatmap":
      return new ColumnHeatmapRow(id, descriptor, sheet);
    case "column.sticker":
      return new ColumnStickerRow(id, descriptor, sheet);
    case "column.task":
      return new ColumnTaskRow(id, descriptor, sheet);
    case "column.event-list":
      return new ColumnEventListRow(id, descriptor, sheet);
    case "column.timezone":
      return new ColumnTimezoneRow(id, descriptor, sheet);
    case "column.weather":
      return new ColumnWeatherRow(id, descriptor, sheet);
    case "column.logs":
      return new ColumnLogsRow(id, descriptor, sheet);
    case "column.timeline":
      return new ColumnTimelineRow(id, descriptor, sheet);

    // canvas slot
    case "canvas.hrule":
      return new CanvasHRuleRow(id, descriptor, sheet);
    case "canvas.points":
      return new CanvasPointsRow(id, descriptor, sheet);
    case "canvas.line":
      return new CanvasLineRow(id, descriptor, sheet);
    case "canvas.bar":
      return new CanvasBarRow(id, descriptor, sheet);
    case "canvas.regression-curve":
      return new CanvasRegressionCurveRow(id, descriptor, sheet);
    case "canvas.regression-band":
      return new CanvasRegressionBandRow(id, descriptor, sheet);
    case "canvas.regression-fill":
      return new CanvasRegressionFillRow(id, descriptor, sheet);

    // timeline[row] + timeline[column] slots (same class per renderer)
    case "timeline[row].event":
    case "timeline[column].event":
      return new TimelineEventRow(id, descriptor, sheet);
    case "timeline[row].span":
    case "timeline[column].span":
      return new TimelineSpanRow(id, descriptor, sheet);
    case "timeline[row].text":
    case "timeline[column].text":
      return new TimelineTextRow(id, descriptor, sheet);
    case "timeline[row].number":
    case "timeline[column].number":
      return new TimelineNumberRow(id, descriptor, sheet);

    // grid slot
    case "grid.event":
      return new GridEventRow(id, descriptor, sheet);
    case "grid.text":
      return new GridTextRow(id, descriptor, sheet);

    default:
      return null;
  }
}
